import React, { useState, useEffect, useRef } from "react";
import { purchaseDashboard, getCampaignNames } from "../../api/Calling";
import { FaFilter, FaSync, FaDownload, FaFilePdf, FaFileExcel, FaChevronDown } from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import { BiDollar } from "react-icons/bi";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import ReactECharts from "echarts-for-react";

import formatDateTime from "../../components/formatDateTime";
import {
  T, PALETTE,
  buildTimelineData, buildTypeData, buildCampaignData, buildProductData,
  buildAreaChartOption, buildPieChartOption, buildProductBarOption, buildCampaignBarOption,
} from "./PurchaseChartOptions";

// ─── Date range calculator ────────────────────────────────────────────────────
const calculateDateRange = (filters) => {
  const today = new Date();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  let startDate = "";
  let endDate = formatDate(today);
  switch (filters.dateRangeType) {
    case "today": startDate = formatDate(today); break;
    case "yesterday":
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = formatDate(yesterday);
      endDate = formatDate(yesterday);
      break;
    case "last15":
      const last15 = new Date(today);
      last15.setDate(last15.getDate() - 15);
      startDate = formatDate(last15);
      break;
    case "last30":
      const last30 = new Date(today);
      last30.setDate(last30.getDate() - 30);
      startDate = formatDate(last30);
      break;
    case "last6months":
      const last6months = new Date(today);
      last6months.setMonth(last6months.getMonth() - 6);
      startDate = formatDate(last6months);
      break;
    case "custom":
      startDate = filters.customStartDate;
      endDate = filters.customEndDate;
      break;
    default: startDate = formatDate(today);
  }
  return { startDate, endDate };
};

// ─── Animated Stat Number ─────────────────────────────────────────────────────
const AnimatedStat = ({ value }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let n = 0;
    const step = Math.max(1, Math.ceil(value / 30));
    const t = setInterval(() => {
      n += step;
      if (n >= value) { setDisplay(value); clearInterval(t); }
      else setDisplay(n);
    }, 18);
    return () => clearInterval(t);
  }, [value]);
  return <span>{display.toLocaleString()}</span>;
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, gradient, icon, glow }) => (
  <div style={{
    background: gradient,
    borderRadius: 20,
    padding: "22px 24px",
    color: "#fff",
    position: "relative",
    overflow: "hidden",
    boxShadow: `0 8px 32px ${glow}40, 0 1px 0 rgba(255,255,255,0.15) inset`,
    transition: "transform 0.2s, box-shadow 0.2s",
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 16px 40px ${glow}55, 0 1px 0 rgba(255,255,255,0.15) inset`; }}
    onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 8px 32px ${glow}40, 0 1px 0 rgba(255,255,255,0.15) inset`; }}
  >
    {/* Decorative circles */}
    <div style={{ position: "absolute", top: -20, right: -20, width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.1)" }} />
    <div style={{ position: "absolute", bottom: -8, right: 10, width: 50, height: 50, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
    <div style={{ position: "absolute", top: 0, left: 24, right: 24, height: 1, background: "rgba(255,255,255,0.2)" }} />
    <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
      <div>
        <p style={{ margin: 0, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", marginBottom: 8 }}>{label}</p>
        <p style={{ margin: 0, fontSize: 38, fontWeight: 900, lineHeight: 1, letterSpacing: "-0.02em" }}><AnimatedStat value={value} /></p>
      </div>
      <div style={{ padding: "10px 11px", background: "rgba(255,255,255,0.15)", borderRadius: 14, border: "1px solid rgba(255,255,255,0.2)", fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{icon}</div>
    </div>
  </div>
);

// ─── Revenue Card ─────────────────────────────────────────────────────────────
const RevCard = ({ currency, amount }) => (
  <div style={{
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
    borderRadius: 18,
    padding: "18px 20px",
    color: "#fff",
    position: "relative",
    overflow: "hidden",
    border: "1px solid rgba(37,99,235,0.25)", // matched to main blue
    boxShadow: "0 8px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.03) inset",
    transition: "transform 0.2s",
  }}
    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
    onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
  >
    <div style={{ position: "absolute", top: -16, right: -16, width: 72, height: 72, borderRadius: "50%", background: "rgba(37,99,235,0.12)" }} />
    <div style={{ position: "absolute", top: 0, left: 20, right: 20, height: 1, background: "linear-gradient(90deg, transparent, rgba(37,99,235,0.5), transparent)" }} />
    <div style={{ position: "relative", zIndex: 1 }}>
      <p style={{ margin: "0 0 6px", fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(148,163,184,0.9)" }}>Revenue · {currency}</p>
      <p style={{ margin: 0, fontSize: 28, fontWeight: 900, letterSpacing: "-0.01em", color: "#f1f5f9" }}>{parseFloat(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
    </div>
  </div>
);

// ─── Chart Card Wrapper ───────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, accent = T.blue, icon, children, style = {} }) => (
  <div style={{
    background: "#fff",
    borderRadius: 24,
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 12px 40px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
    ...style,
  }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />
    <div style={{ position: "absolute", top: 0, right: 0, width: 280, height: 200, pointerEvents: "none", background: `radial-gradient(circle at 80% 0%, ${accent}0d 0%, transparent 60%)` }} />
    <div style={{ position: "relative", zIndex: 1, padding: "26px 28px 22px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20 }}>
        {icon && (
          <div style={{ padding: "9px 10px", borderRadius: 12, background: `${accent}14`, border: `1px solid ${accent}22`, fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{icon}</div>
        )}
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900, color: "#1e293b", letterSpacing: "-0.02em" }}>{title}</h3>
          {subtitle && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#94a3b8", fontWeight: 500, lineHeight: 1.5 }}>{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  </div>
);

// ─── Empty / Loading State ────────────────────────────────────────────────────
const ChartEmpty = ({ loading, height = 260 }) => (
  <div style={{ height, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
    {loading ? (
      <>
        <div style={{ width: 38, height: 38, borderRadius: "50%", border: "3.5px solid #dde5ff", borderTopColor: "#2563EB", animation: "spin 0.8s linear infinite" }} />
        <p style={{ margin: 0, fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>Loading data…</p>
      </>
    ) : (
      <>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg,#e2e8f0,#f1f5f9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <BiDollar style={{ color: "#cbd5e1", fontSize: 22 }} />
        </div>
        <p style={{ margin: 0, fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>No data for this period</p>
      </>
    )}
  </div>
);

// ─── Section Label ────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "8px 0" }}>
    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)" }} />
    <span style={{ fontSize: 10, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "#9ca3af", padding: "0 4px" }}>{children}</span>
    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)" }} />
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
const PurchaseDashboard = () => {
  const defaultFilters = {
    dateRangeType: "today",
    customStartDate: "",
    customEndDate: "",
    typeFilter: "",
    campaignFilter: "",
    search: "",
  };

  const [tempFilters, setTempFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [purchaseData, setPurchaseData] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef(null);

  const stats = {
    totalPurchases: purchaseData.length,
    successfulPurchases: purchaseData.filter(p => p.p_status === "success").length,
    pidPurchases: purchaseData.filter(p => p.p_type === "pid").length,
    organicPurchases: purchaseData.filter(p => p.p_type === "organic").length,
  };

  const revenueByCurrency = purchaseData.reduce((acc, p) => {
    const currency = p.p_currency || "UNKNOWN";
    if (!acc[currency]) acc[currency] = 0;
    acc[currency] += p.p_amount || 0;
    return acc;
  }, {});

  const timelineData = buildTimelineData(purchaseData);
  const revenueByDateCurrency = (() => {
    const map = {};
    purchaseData.forEach(p => {
      if (p.p_status !== "success") return;
      const day = (p.createdAt || "").slice(5, 10);
      const cur = p.p_currency || "UNKNOWN";
      if (!map[day]) map[day] = {};
      map[day][cur] = (map[day][cur] || 0) + (p.p_amount || 0);
    });
    return map;
  })();
  const typeData = buildTypeData(purchaseData);
  const campaignData = buildCampaignData(purchaseData);
  const productData = buildProductData(purchaseData);
  const hasData = !loading && purchaseData.length > 0;

  useEffect(() => { fetchCampaigns(); }, []);
  useEffect(() => { fetchData(); }, [appliedFilters]);
  useEffect(() => {
    const handle = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target))
        setShowDownloadMenu(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await getCampaignNames();
      if (res.status) setCampaigns(res.data || []);
    } catch (err) { console.error(err); }
  };

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    const { startDate, endDate } = calculateDateRange(filters);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (filters.typeFilter) params.append("type", filters.typeFilter);
    if (filters.typeFilter === "pid" && filters.campaignFilter) params.append("p_id", filters.campaignFilter);
    if (filters.search) params.append("search", filters.search);
    return `?${params.toString()}`;
  };

  const fetchData = async () => {
    setLoading(true); setError("");
    try {
      const res = await purchaseDashboard(buildQueryParams(appliedFilters));
      if (res.status) setPurchaseData(res.data || []);
      else { setError(res.message || "Failed to fetch data"); setPurchaseData([]); }
    } catch { setError("Network error. Please try again."); setPurchaseData([]); }
    finally { setLoading(false); }
  };

  const handleDownloadPDF = () => {
    setShowDownloadMenu(false);
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14); doc.setFont("helvetica", "bold");
    doc.text("Purchase Dashboard Report", 14, 15);
    doc.setFontSize(9); doc.setFont("helvetica", "normal");
    doc.text(`Generated: ${formatDateTime(new Date(), "DD-MM-YYYY")} (IST)`, 14, 22);
    autoTable(doc, {
      startY: 28,
      head: [["Date", "Product", "Order ID", "Type", "Campaign", "PID", "Amount", "Status"]],
      body: purchaseData.map(p => [
        formatDateTime(p.createdAt, "DD-MM-YYYY HH:mm:ss"), p.p_name || "N/A", p.p_order_id || "N/A",
        p.p_type || "N/A", p.p_campaign || "N/A", p.p_pid || "N/A",
        `${p.p_amount || 0} ${p.p_currency || ""}`, p.p_status || "N/A",
      ]),
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: "bold", halign: "center", fontSize: 8 },
      bodyStyles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 35 }, 1: { cellWidth: 30 }, 2: { cellWidth: 35 },
        3: { halign: "center", cellWidth: 20 }, 4: { cellWidth: 30 },
        5: { halign: "center", cellWidth: 25 }, 6: { halign: "right", cellWidth: 25 },
        7: { halign: "center", cellWidth: 20 },
      },
      alternateRowStyles: { fillColor: [239, 246, 255] },
      margin: { left: 14, right: 14 },
    });
    doc.save(`Purchase_Report_${formatDateTime(new Date(), "DD-MM-YYYY")}.pdf`);
  };

  const handleDownloadExcel = () => {
    setShowDownloadMenu(false);
    const wsData = [
      ["Date", "Product", "Order ID", "Type", "Campaign", "PID", "Amount", "Currency", "Status"],
      ...purchaseData.map(p => [
        formatDateTime(p.createdAt, "DD-MM-YYYY HH:mm:ss"),
        p.p_name || "N/A", p.p_order_id || "N/A",
        p.p_type || "N/A", p.p_campaign || "N/A", p.p_pid || "N/A",
        p.p_amount || 0, p.p_currency || "", p.p_status || "N/A",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 20 }, { wch: 25 }, { wch: 30 }, { wch: 12 }, { wch: 25 }, { wch: 18 }, { wch: 12 }, { wch: 10 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws, "Purchase Report");
    XLSX.writeFile(wb, `Purchase_Report_${formatDateTime(new Date(), "DD-MM-YYYY")}.xlsx`);
  };

  const handleApplyFilters = () => setAppliedFilters({ ...tempFilters });
  const handleResetFilters = () => { setTempFilters(defaultFilters); setAppliedFilters(defaultFilters); };

  // Chart1 mini stats
  const chart1Stats = (() => {
    if (!hasData || timelineData.length === 0) return null;
    const totalSuccess = timelineData.reduce((s, d) => s + d.success, 0);
    const totalFailed = timelineData.reduce((s, d) => s + d.failed, 0);
    const peakDay = timelineData.reduce((b, d) => d.total > (b?.total ?? 0) ? d : b, null);
    return { totalSuccess, totalFailed, peakDay };
  })();

  const areaOpt = hasData && timelineData.length > 0 ? buildAreaChartOption(timelineData, revenueByDateCurrency) : null;
  const pieOpt = hasData && typeData.length > 0 ? buildPieChartOption(typeData) : null;
  const productOpt = hasData && productData.length > 0 ? buildProductBarOption(productData) : null;
  const campaignOpt = hasData && campaignData.length > 0 ? buildCampaignBarOption(campaignData) : null;

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .c-appear { animation: fadeUp 0.5s cubic-bezier(.22,.68,0,1.15) both; }
        .c-appear:nth-child(1){animation-delay:.06s}
        .c-appear:nth-child(2){animation-delay:.14s}
        .c-appear:nth-child(3){animation-delay:.22s}
        .c-appear:nth-child(4){animation-delay:.30s}
        .c-appear:nth-child(5){animation-delay:.38s}

        .f-select {
          width:100%; padding:11px 14px;
          border:2px solid #e5e7eb; border-radius:12px;
          background:#fafbff; color:#111827;
          font-size:13px; font-weight:600; outline:none;
          transition:border-color 0.18s, box-shadow 0.18s;
          appearance:none; cursor:pointer;
        }
        .f-select:focus { border-color:#2563EB; box-shadow:0 0 0 4px rgba(37,99,235,0.1); }
        .f-label {
          display:block; font-size:10px; font-weight:800;
          letter-spacing:0.1em; text-transform:uppercase;
          color:#9ca3af; margin-bottom:7px;
        }
        .btn-primary {
          display:inline-flex; align-items:center; gap:9px;
          padding:11px 20px; border-radius:12px; border:none; cursor:pointer;
          font-size:13.5px; font-weight:800; color:#fff;
          background:linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%);
          box-shadow:0 4px 16px rgba(37,99,235,0.32);
          transition:transform 0.15s, box-shadow 0.15s;
        }
        .btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(37,99,235,0.42); }
        .btn-green {
          display:inline-flex; align-items:center; gap:9px;
          padding:11px 20px; border-radius:12px; border:none; cursor:pointer;
          font-size:13.5px; font-weight:800; color:#fff;
          background:linear-gradient(135deg, #10B981 0%, #059669 100%);
          box-shadow:0 4px 16px rgba(16,185,129,0.28);
          transition:transform 0.15s, box-shadow 0.15s;
        }
        .btn-green:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(16,185,129,0.38); }
        .btn-ghost {
          display:inline-flex; align-items:center; gap:8px;
          padding:11px 18px; border-radius:12px; cursor:pointer;
          font-size:13px; font-weight:700; color:#6b7280;
          background:#f9fafb; border:2px solid #e5e7eb;
          transition:all 0.15s;
        }
        .btn-ghost:hover { background:#f3f4f6; color:#374151; border-color:#d1d5db; }
        .dl-dropdown {
          position:absolute; top:calc(100% + 8px); right:0; z-index:50;
          background:#fff; border:1px solid #e5e7eb; border-radius:16px;
          box-shadow:0 20px 60px rgba(0,0,0,0.12),0 4px 16px rgba(0,0,0,0.06);
          overflow:hidden; min-width:196px;
          animation: fadeUp 0.18s ease both;
        }
        .dl-item {
          display:flex; align-items:center; gap:10px;
          padding:13px 18px; font-size:13px; font-weight:700;
          color:#374151; cursor:pointer;
          transition:background 0.12s,color 0.12s;
          border-bottom:1px solid #f9fafb;
        }
        .dl-item:last-child { border-bottom:none; }
        .dl-item.pdf:hover   { color:#ef4444; background:#fff5f5; }
        .dl-item.excel:hover { color:#16a34a; background:#f0fdf4; }
        .chart1-legend-pill {
          display:inline-flex; align-items:center; gap:6px;
          padding:5px 13px; border-radius:999px;
          font-size:11.5px; font-weight:700; line-height:1;
        }
        .mini-chip {
          background:#f8fafc; border:1px solid #e2e8f0;
          border-radius:12px; padding:10px 14px;
          transition: box-shadow 0.18s;
        }
        .mini-chip:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.07); }
      `}</style>

      <div style={{ marginBottom: 40, fontFamily: "system-ui, -apple-system, sans-serif" }}>

        {/* ══ HEADER ════════════════════════════════════════════════════════════ */}
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 20, marginBottom: 28, position: "relative", zIndex: 10 }} className="c-appear">
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ padding: 12, borderRadius: 18, background: "linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)", boxShadow: "0 6px 20px rgba(37,99,235,0.38)" }}>
              <BiDollar style={{ fontSize: 24, color: "#fff" }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 30, fontWeight: 900, color: "#0f172a", letterSpacing: "-0.03em", lineHeight: 1 }}>Purchase Dashboard</h1>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>Monitor and analyze purchase transactions</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ position: "relative" }} ref={downloadMenuRef}>
              <button className="btn-green" onClick={() => setShowDownloadMenu(p => !p)}>
                <FaDownload style={{ fontSize: 13 }} /> Download
                <FaChevronDown style={{ fontSize: 11, transition: "transform 0.2s", transform: showDownloadMenu ? "rotate(180deg)" : "rotate(0deg)" }} />
              </button>
              {showDownloadMenu && (
                <div className="dl-dropdown">
                  <div className="dl-item pdf" onClick={handleDownloadPDF}><FaFilePdf style={{ fontSize: 14 }} /> Download PDF</div>
                  <div className="dl-item excel" onClick={handleDownloadExcel}><FaFileExcel style={{ fontSize: 14 }} /> Download Excel</div>
                </div>
              )}
            </div>
            <button className="btn-primary" onClick={fetchData}>
              <MdRefresh style={{ fontSize: 16, animation: loading ? "spin 0.8s linear infinite" : "none" }} /> Refresh
            </button>
          </div>
        </div>

        {/* ══ VIBRANT STAT CARDS ════════════════════════════════════════════════ */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 16 }} className="c-appear">
          <StatCard label="Total Purchases" value={stats.totalPurchases} icon="📦" gradient="linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)" glow="#2563eb" />
          <StatCard label="Successful" value={stats.successfulPurchases} icon="✅" gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)" glow="#10b981" />
          <StatCard label="Organic" value={stats.organicPurchases} icon="🌿" gradient="linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)" glow="#8b5cf6" />
          <StatCard label="PID" value={stats.pidPurchases} icon="🎯" gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" glow="#f59e0b" />
        </div>

        {/* ══ REVENUE CARDS ══════════════════════════════════════════════════════ */}
        {Object.keys(revenueByCurrency).length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14, marginBottom: 28 }} className="c-appear">
            {Object.entries(revenueByCurrency).map(([currency, amount]) => (
              <RevCard key={currency} currency={currency} amount={amount} />
            ))}
          </div>
        )}

        {/* ══ FILTERS ════════════════════════════════════════════════════════════ */}
        <div style={{ background: "#fff", borderRadius: 24, border: "1px solid #f1f5f9", padding: "24px 28px", marginBottom: 28, boxShadow: "0 1px 3px rgba(0,0,0,0.04),0 12px 40px rgba(0,0,0,0.06)" }} className="c-appear">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <div style={{ padding: "9px 10px", borderRadius: 12, background: "#eff6ff", border: "1px solid #dbeafe" }}>
              <FaFilter style={{ color: "#2563EB", fontSize: 11 }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 900, color: "#1e293b", letterSpacing: "-0.01em" }}>Filters</h2>
              <p style={{ margin: "3px 0 0", fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>Narrow results by date, type &amp; campaign</p>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 16 }}>
            <div>
              <label className="f-label">📅 Date Range</label>
              <select className="f-select" value={tempFilters.dateRangeType}
                onChange={e => setTempFilters({ ...tempFilters, dateRangeType: e.target.value })}>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last15">Last 15 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="last6months">Last 6 Months</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            <div>
              <label className="f-label">🔗 Type</label>
              <select className="f-select" value={tempFilters.typeFilter}
                onChange={e => setTempFilters({ ...tempFilters, typeFilter: e.target.value, campaignFilter: "" })}>
                <option value="">All Types</option>
                <option value="organic">Organic</option>
                <option value="pid">PID</option>
              </select>
            </div>
            {tempFilters.typeFilter === "pid" && (
              <div>
                <label className="f-label">🎯 Campaign</label>
                <select className="f-select" value={tempFilters.campaignFilter}
                  onChange={e => setTempFilters({ ...tempFilters, campaignFilter: e.target.value })}>
                  <option value="">All Campaigns</option>
                  {campaigns.map(c => (
                    <option key={c.p_id} value={c.p_id}>
                      {c.camp_name} ({c.p_id})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {tempFilters.dateRangeType === "custom" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16, padding: 16, background: "#f8fafc", borderRadius: 16, border: "1px solid #f1f5f9" }}>
              <div>
                <label className="f-label">Start Date</label>
                <input type="date" className="f-select" value={tempFilters.customStartDate}
                  onChange={e => setTempFilters({ ...tempFilters, customStartDate: e.target.value })} />
              </div>
              <div>
                <label className="f-label">End Date</label>
                <input type="date" className="f-select" value={tempFilters.customEndDate}
                  onChange={e => setTempFilters({ ...tempFilters, customEndDate: e.target.value })} />
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
            <button className="btn-primary" onClick={handleApplyFilters}><FaFilter style={{ fontSize: 11 }} /> Apply Filters</button>
            <button className="btn-ghost" onClick={handleResetFilters}><FaSync style={{ fontSize: 11 }} /> Reset</button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ marginBottom: 20, padding: "16px 20px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <p style={{ margin: 0, color: "#e11d48", fontWeight: 600, fontSize: 13 }}>{error}</p>
          </div>
        )}

        {/* ══ CHARTS ══════════════════════════════════════════════════════════════ */}
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          <SectionLabel>Analytics Overview</SectionLabel>

          {/* ── CHART 1: Area Chart ───────────────────────────────────────────── */}
          <div className="c-appear" style={{
            background: "#fff", borderRadius: 28, overflow: "hidden", position: "relative",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04),0 12px 40px rgba(0,0,0,0.08),0 0 0 1px rgba(0,0,0,0.04)",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#2563EB,#10B981,#8B5CF6)" }} />
            <div style={{ position: "absolute", top: 0, right: 0, width: 340, height: 260, pointerEvents: "none", background: "radial-gradient(circle at 80% 0%,#2563EB0d 0%,transparent 60%)" }} />

            <div style={{ position: "relative", zIndex: 1, padding: "26px 28px 22px" }}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div style={{ padding: "9px 10px", borderRadius: 13, background: "#2563EB14", border: "1px solid #2563EB22", fontSize: 16, lineHeight: 1, flexShrink: 0 }}>📈</div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 900, color: "#1e293b", letterSpacing: "-0.02em" }}>Purchases &amp; Revenue Over Time</h3>
                    <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "#94a3b8", fontWeight: 500 }}>Daily success / failed · hover <strong style={{ color: "#2563EB" }}>Revenue</strong> for per-currency breakdown</p>
                  </div>
                </div>
                {/* Pill legend */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {[
                    { color: "#10B981", label: "Successful", dashed: false },
                    { color: "#f43f5e", label: "Failed", dashed: false },
                    { color: "#2563EB", label: "Revenue", dashed: true },
                  ].map(({ color, label, dashed }) => (
                    <div key={label} className="chart1-legend-pill"
                      style={{ background: color + "12", border: `1px solid ${color}28`, color }}>
                      <svg width="22" height="6" style={{ flexShrink: 0 }}>
                        {dashed
                          ? <line x1="0" y1="3" x2="22" y2="3" stroke={color} strokeWidth="2.5" strokeDasharray="5 3" />
                          : <line x1="0" y1="3" x2="22" y2="3" stroke={color} strokeWidth="2.5" />
                        }
                      </svg>
                      {label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Mini stat chips */}
              {chart1Stats && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 18 }}>
                  {[
                    { label: "Successful", value: chart1Stats.totalSuccess.toLocaleString(), color: "#10B981", bg: "#f0fdf4", border: "#bbf7d0" },
                    { label: "Failed", value: chart1Stats.totalFailed.toLocaleString(), color: "#f43f5e", bg: "#fff1f2", border: "#fecdd3" },
                    { label: "Peak Day", value: chart1Stats.peakDay?.date ?? "—", color: "#2563EB", bg: "#eff6ff", border: "#bfdbfe" },
                  ].map(({ label, value, color, bg, border }) => (
                    <div key={label} className="mini-chip" style={{ background: bg, border: `1px solid ${border}` }}>
                      <p style={{ margin: 0, fontSize: 9.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>{label}</p>
                      <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 900, color, lineHeight: 1 }}>{value}</p>
                    </div>
                  ))}
                </div>
              )}

              {areaOpt
                ? <ReactECharts option={areaOpt} style={{ height: 300, width: "100%" }} notMerge={true} lazyUpdate={true} />
                : <ChartEmpty loading={loading} height={300} />
              }
            </div>
          </div>

          {/* ── CHART 2 + CHART 3 (side by side) ─────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 20 }} className="c-appear">
            <ChartCard title="Purchase Type Distribution" subtitle="Organic vs PID split" accent="#F59E0B" icon="🍩">
              {pieOpt ? (
                <>
                  <ReactECharts option={pieOpt} style={{ height: 240, width: "100%" }} notMerge={true} lazyUpdate={true} />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginTop: 6 }}>
                    {typeData.map((entry, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "5px 13px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                        background: PALETTE[i % PALETTE.length] + "12",
                        color: PALETTE[i % PALETTE.length],
                        border: `1px solid ${PALETTE[i % PALETTE.length]}30`,
                      }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: PALETTE[i % PALETTE.length], boxShadow: `0 0 5px ${PALETTE[i % PALETTE.length]}`, display: "inline-block" }} />
                        {entry.name} · {entry.value.toLocaleString()}
                      </div>
                    ))}
                  </div>
                </>
              ) : <ChartEmpty loading={loading} />}
            </ChartCard>

            <ChartCard title="Top Products by Revenue" subtitle="Best performing products — hover to see per-currency breakdown" accent="#8B5CF6" icon="🏆">
              {productOpt
                ? <ReactECharts option={productOpt} style={{ height: 280, width: "100%" }} notMerge={true} lazyUpdate={true} />
                : <ChartEmpty loading={loading} height={280} />
              }
            </ChartCard>
          </div>

          {/* ── CHART 4: Campaign Performance ────────────────────────────────── */}
          <ChartCard
            title="Campaign Performance"
            subtitle="Top campaigns by purchase volume and revenue — hover Revenue to see per-currency breakdown"
            accent="#10B981" icon="🚀"
            style={{ marginTop: 0 }}
          >
            <div className="c-appear">
              {campaignOpt
                ? <ReactECharts option={campaignOpt} style={{ height: 340, width: "100%" }} notMerge={true} lazyUpdate={true} />
                : <ChartEmpty loading={loading} height={340} />
              }
            </div>
          </ChartCard>

        </div>
      </div>
    </>
  );
};

export default PurchaseDashboard;